import { useEffect, useCallback, RefObject, useRef } from 'react';
import { logger } from '@/lib/logger';

interface UseZoomGesturesProps {
  containerRef: RefObject<HTMLDivElement>;
  zoom: number;
  position: { x: number; y: number };
  setZoom: (zoom: number) => void;
  setPosition: (position: { x: number; y: number }) => void;
  onPositionChange?: (x: number, y: number) => void;
  minZoom?: number;
  maxZoom?: number;
  isPanningEnabled?: boolean;
  disablePan?: boolean;
  imageSize?: { width: number; height: number };
  isAnimating?: boolean; // 🔧 CRITICAL FIX: Flag to block gesture updates during viewport animations
}

export function useZoomGestures({
  containerRef,
  zoom,
  position,
  setZoom,
  setPosition,
  onPositionChange,
  minZoom = 0.2,
  maxZoom = 10,
  isPanningEnabled = false,
  disablePan = false,
  imageSize,
  isAnimating = false, // 🔧 CRITICAL FIX: Default to false, blocks updates when true
}: UseZoomGesturesProps) {
  // ---------- Refs to track ongoing gesture state ----------
  const initialTouchDistanceRef = useRef<number>(0);
  const initialZoomRef = useRef<number>(0);
  const lastMidpointRef = useRef<{ x: number; y: number }>({ x: 0, y: 0 });
  const initialMidpointRef = useRef<{ x: number; y: number }>({ x: 0, y: 0 });
  const initialRelativeRef = useRef<{ x: number; y: number }>({ x: 0, y: 0 });
  const isGesturingRef = useRef<boolean>(false);

  // ---------- Mouse wheel / track-pad pinch ----------
  const handleWheel = useCallback(
    (e: WheelEvent) => {
      // 🔧 CRITICAL FIX: Block gesture updates during viewport animations
      if (isAnimating) {
        logger.log('[useZoomGestures] ⛔ handleWheel blocked - animation in progress');
        return;
      }

      e.preventDefault();
      const container = containerRef.current;
      if (!container) return;

      const rect = container.getBoundingClientRect();
      const mouseX = e.clientX - rect.left;
      const mouseY = e.clientY - rect.top;

      // ctrlKey/metaKey indicates pinch gesture on macOS trackpads
      if (e.ctrlKey || e.metaKey) {
        const rawDelta = -e.deltaY;
        const CLAMP = 100; // Prevent wild jumps
        const clamped = Math.max(Math.min(rawDelta, CLAMP), -CLAMP);
        const scaleFactor = 1 + clamped / 250; // ~±40 % per event for snappier desktop zoom
        const newZoom = Math.min(Math.max(zoom * scaleFactor, minZoom), maxZoom);

        // Preserve focal point under cursor
        const relativeX = (mouseX - position.x) / zoom;
        const relativeY = (mouseY - position.y) / zoom;
        const newPos = {
          x: mouseX - relativeX * newZoom,
          y: mouseY - relativeY * newZoom,
        };

        setZoom(newZoom);
        setPosition(newPos);
        onPositionChange?.(newPos.x, newPos.y);
      } else {
        // Two-finger pan on track-pad
        const MAX_DELTA = 300;
        const deltaX = Math.max(Math.min(e.deltaX, MAX_DELTA), -MAX_DELTA);
        const deltaY = Math.max(Math.min(e.deltaY, MAX_DELTA), -MAX_DELTA);
        const newPos = { x: position.x - deltaX, y: position.y - deltaY };
        setPosition(newPos);
        onPositionChange?.(newPos.x, newPos.y);
      }
    },
    [
      containerRef,
      zoom,
      position,
      setZoom,
      setPosition,
      onPositionChange,
      minZoom,
      maxZoom,
      isAnimating,
    ],
  );

  // ---------- Optional mouse-drag panning (hand-tool) ----------
  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      // 🔧 CRITICAL FIX: Block gesture updates during viewport animations
      if (isAnimating) {
        logger.log('[useZoomGestures] ⛔ handleMouseDown blocked - animation in progress');
        return;
      }

      if (disablePan || !isPanningEnabled) {
        return;
      }
      const container = containerRef.current;
      if (!container) return;

      e.preventDefault();
      e.stopPropagation();
      const startX = e.clientX;
      const startY = e.clientY;
      const startPosX = position.x;
      const startPosY = position.y;

      const onMouseMove = (ev: MouseEvent) => {
        const dx = ev.clientX - startX;
        const dy = ev.clientY - startY;
        const newPos = { x: startPosX + dx, y: startPosY + dy };
        setPosition(newPos);
        onPositionChange?.(newPos.x, newPos.y);
      };
      const onMouseUp = () => {
        document.removeEventListener('mousemove', onMouseMove);
        document.removeEventListener('mouseup', onMouseUp);
      };
      document.addEventListener('mousemove', onMouseMove);
      document.addEventListener('mouseup', onMouseUp);
    },
    [
      containerRef,
      position,
      setPosition,
      onPositionChange,
      disablePan,
      isPanningEnabled,
      isAnimating,
    ],
  );

  // ---------- Touch pinch & pan ----------
  // Track single touch pan state
  const singleTouchStartRef = useRef<{ x: number; y: number } | null>(null);
  const singleTouchPositionRef = useRef<{ x: number; y: number } | null>(null);

  const handleTouchStart = useCallback(
    (e: TouchEvent) => {
      // 🔧 CRITICAL FIX: Block gesture updates during viewport animations
      if (isAnimating) {
        logger.log('[useZoomGestures] ⛔ handleTouchStart blocked - animation in progress');
        return;
      }

      // Handle single-finger pan when pan tool is active
      if (e.touches.length === 1 && isPanningEnabled && !disablePan) {
        e.preventDefault();
        const touch = e.touches[0];
        singleTouchStartRef.current = { x: touch.clientX, y: touch.clientY };
        singleTouchPositionRef.current = { x: position.x, y: position.y };
        return;
      }

      // Handle two-finger pinch/pan
      if (e.touches.length !== 2) return;
      e.preventDefault();
      const container = containerRef.current;
      if (!container) return;

      const rect = container.getBoundingClientRect();
      const [t1, t2] = [e.touches[0], e.touches[1]];
      initialTouchDistanceRef.current = Math.hypot(
        t2.clientX - t1.clientX,
        t2.clientY - t1.clientY,
      );
      initialZoomRef.current = zoom;

      const midX = (t1.clientX + t2.clientX) / 2;
      const midY = (t1.clientY + t2.clientY) / 2;
      initialMidpointRef.current = { x: midX - rect.left, y: midY - rect.top };
      lastMidpointRef.current = initialMidpointRef.current;
      initialRelativeRef.current = {
        x: (initialMidpointRef.current.x - position.x) / zoom,
        y: (initialMidpointRef.current.y - position.y) / zoom,
      };
      isGesturingRef.current = true;
    },
    [containerRef, position, zoom, isPanningEnabled, disablePan, isAnimating],
  );

  const handleTouchMove = useCallback(
    (e: TouchEvent) => {
      // 🔧 CRITICAL FIX: Block gesture updates during viewport animations
      if (isAnimating) {
        logger.log('[useZoomGestures] ⛔ handleTouchMove blocked - animation in progress');
        return;
      }

      // Handle single-finger pan when pan tool is active
      if (
        e.touches.length === 1 &&
        singleTouchStartRef.current &&
        singleTouchPositionRef.current &&
        isPanningEnabled &&
        !disablePan
      ) {
        e.preventDefault();
        const touch = e.touches[0];
        const dx = touch.clientX - singleTouchStartRef.current.x;
        const dy = touch.clientY - singleTouchStartRef.current.y;
        const newPos = {
          x: singleTouchPositionRef.current.x + dx,
          y: singleTouchPositionRef.current.y + dy,
        };
        setPosition(newPos);
        onPositionChange?.(newPos.x, newPos.y);
        return;
      }

      // Handle two-finger pinch/pan
      if (!isGesturingRef.current || e.touches.length !== 2) return;
      e.preventDefault();
      const container = containerRef.current;
      if (!container) return;
      const rect = container.getBoundingClientRect();
      const [t1, t2] = [e.touches[0], e.touches[1]];

      const newDistance = Math.hypot(t2.clientX - t1.clientX, t2.clientY - t1.clientY);
      const zoomRatio = newDistance / initialTouchDistanceRef.current;
      const SCALE_THRESHOLD = 0.05; // 5%
      const isPinching = Math.abs(zoomRatio - 1) > SCALE_THRESHOLD;

      const currentMid = {
        x: (t1.clientX + t2.clientX) / 2 - rect.left,
        y: (t1.clientY + t2.clientY) / 2 - rect.top,
      };

      let newZoom = zoom;
      if (isPinching) {
        const zoomFactor = 1 + (zoomRatio - 1) * 0.5; // dampen
        newZoom = Math.min(Math.max(initialZoomRef.current * zoomFactor, minZoom), maxZoom);
      }

      const zoomAdjustedPos = {
        x: currentMid.x - initialRelativeRef.current.x * newZoom,
        y: currentMid.y - initialRelativeRef.current.y * newZoom,
      };

      const panDelta = {
        x: currentMid.x - lastMidpointRef.current.x,
        y: currentMid.y - lastMidpointRef.current.y,
      };

      const newPos = isPinching
        ? zoomAdjustedPos
        : { x: position.x + panDelta.x, y: position.y + panDelta.y };

      if (isPinching) setZoom(newZoom);
      setPosition(newPos);
      onPositionChange?.(newPos.x, newPos.y);
      lastMidpointRef.current = currentMid;
    },
    [
      containerRef,
      zoom,
      position,
      setZoom,
      setPosition,
      onPositionChange,
      minZoom,
      maxZoom,
      isPanningEnabled,
      disablePan,
      isAnimating,
    ],
  );

  const handleTouchEnd = useCallback((e: TouchEvent) => {
    if (e.touches.length < 2) {
      isGesturingRef.current = false;
    }
    // Reset single touch refs when touch ends
    if (e.touches.length === 0) {
      singleTouchStartRef.current = null;
      singleTouchPositionRef.current = null;
    }
  }, []);

  // ---------- Add / remove listeners ----------
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    container.addEventListener('wheel', handleWheel, { passive: false });
    container.addEventListener('touchstart', handleTouchStart, { passive: false });
    container.addEventListener('touchmove', handleTouchMove, { passive: false });
    container.addEventListener('touchend', handleTouchEnd, { passive: false });
    container.addEventListener('touchcancel', handleTouchEnd, { passive: false });
    return () => {
      container.removeEventListener('wheel', handleWheel);
      container.removeEventListener('touchstart', handleTouchStart);
      container.removeEventListener('touchmove', handleTouchMove);
      container.removeEventListener('touchend', handleTouchEnd);
      container.removeEventListener('touchcancel', handleTouchEnd);
    };
  }, [containerRef, handleWheel, handleTouchStart, handleTouchMove, handleTouchEnd]);

  // ---------- fitToScreen helper ----------
  const fitToScreen = useCallback(() => {
    const container = containerRef.current;
    if (!container) return;

    // Use provided image size or fallback to querying the image element
    let imageWidth: number;
    let imageHeight: number;

    if (imageSize && imageSize.width && imageSize.height) {
      imageWidth = imageSize.width;
      imageHeight = imageSize.height;
    } else {
      const imageElement = container.querySelector('img');
      if (!imageElement) return;
      imageWidth = imageElement.naturalWidth;
      imageHeight = imageElement.naturalHeight;
    }

    if (!imageWidth || !imageHeight) {
      logger.warn('fitToScreen: No image dimensions available');
      return;
    }

    const containerWidth = container.clientWidth;
    const containerHeight = container.clientHeight;

    logger.log('fitToScreen calculation:', {
      container: `${containerWidth}x${containerHeight}`,
      image: `${imageWidth}x${imageHeight}`,
    });

    const scaleX = containerWidth / imageWidth;
    const scaleY = containerHeight / imageHeight;
    const scale = Math.min(scaleX, scaleY) * 0.9; // 90% to add some padding

    const constrainedZoom = Math.min(Math.max(scale, minZoom), maxZoom);
    const x = (containerWidth - imageWidth * constrainedZoom) / 2;
    const y = (containerHeight - imageHeight * constrainedZoom) / 2;

    logger.log('fitToScreen result:', {
      scale: constrainedZoom,
      position: { x, y },
      percentageZoom: Math.round(constrainedZoom * 100),
    });

    setZoom(constrainedZoom);
    setPosition({ x, y });
    onPositionChange?.(x, y);
  }, [containerRef, setZoom, setPosition, onPositionChange, minZoom, maxZoom, imageSize]);

  return {
    handleWheel,
    handleMouseDown,
    handleTouchStart,
    handleTouchMove,
    handleTouchEnd,
    fitToScreen,
  };
}
